import { useRef } from 'react'
import { Button, Card, Col, Form, FormInstance, Row } from 'antd'
import { Flex } from '@weareberyl/design-system'
import type { DeepPartial } from '@apollo/client/utilities'
import type { FormProviderProps } from 'antd/lib/form/context'

import type { BaseFormValues } from './formTypes'
import {
  type SchemeAddonInput,
  type SchemeAddon,
} from '~/gql/generated/graphql'
import { transformCurrencyFields } from '../Product/utils'
import AddonDetailsForm from './AddonDetailsForm'
import { tailLayout } from '~/constants/forms'

export type AddonFormProps = {
  loading?: boolean
  initialValues?: DeepPartial<SchemeAddon>
  onFinish: (schemeAddon: SchemeAddonInput) => void
}
const AddonForm = ({ initialValues, loading, onFinish }: AddonFormProps) => {
  // Define refs for nessted forms for imperative control from this scope (reset button)
  const baseFormRef = useRef<FormInstance<BaseFormValues>>(null)

  // Extract FormattedAmount.amount for formatted amounts in initialValues
  const transformedInitiaValues: SchemeAddonInput = transformCurrencyFields(
    initialValues ?? {},
  )

  const onFormFinish: FormProviderProps['onFormFinish'] = async (
    name,
    { forms },
  ) => {
    // submitForm is just a submitable form, when it's submitted, validate and combine*
    // the fields from the details form
    // * keeping consitent pattern with ProductForm, which does combine data from a few
    // sub-forms. That's not needed here yet, but could be added if neccesary.
    if (name === 'submitForm') {
      let results: [BaseFormValues]
      try {
        // Run validations concurrently to show all errors at once;
        // sequential validation would stop early and hide later errors.
        results = await Promise.all([forms.addonForm.validateFields()])
      } catch {
        // No need to handle, form will display error messages
        return
      }

      const [base] = results

      const data = { ...base }

      onFinish(data)
    }
  }

  const onReset = () => {
    baseFormRef.current?.resetFields()
  }

  return (
    <Form.Provider onFormFinish={onFormFinish}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <AddonDetailsForm
            loading={loading}
            initialValues={transformedInitiaValues}
            formRef={baseFormRef}
          />
        </Col>
      </Row>

      <Form name="submitForm">
        <Card>
          <Form.Item {...tailLayout}>
            <Flex style={{ gap: '10px' }}>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save
              </Button>
              <Button type="dashed" onClick={onReset}>
                Reset
              </Button>
            </Flex>
          </Form.Item>
        </Card>
      </Form>
    </Form.Provider>
  )
}

export default AddonForm
